import { useState, useEffect, useCallback } from 'react';
import liff from '@line/liff';
import { addLog } from '@/utils/debugLogger';
import { persistEnvironment, isValidEnvironment, parseLiffStateParams } from '@/config/apiDomains';

export interface LiffProfile {
  userId: string;
  displayName: string;
  pictureUrl?: string;
  statusMessage?: string;
}

export interface LiffState {
  isInitialized: boolean;
  isLoggedIn: boolean;
  isInClient: boolean;
  profile: LiffProfile | null;
  email: string | null;
  accessToken: string | null;
  idToken: string | null;
  error: Error | null;
  isLoading: boolean;
}

export interface UseLiffReturn extends LiffState {
  login: () => void;
  logout: () => void;
  closeLiff: () => void;
  openWindow: (url: string, external?: boolean) => void;
}

const initialState: LiffState = {
  isInitialized: false,
  isLoggedIn: false,
  isInClient: false,
  profile: null,
  email: null,
  accessToken: null,
  idToken: null,
  error: null,
  isLoading: true,
};

export const useLiff = (): UseLiffReturn => {
  const [state, setState] = useState<LiffState>(initialState);

  useEffect(() => {
    const initLiff = async () => {
      try {
        const liffId = import.meta.env.VITE_LIFF_ID;

        if (!liffId || liffId === 'xxxx-xxxxxx') {
          throw new Error('LIFF IDが設定されていません。環境変数を確認してください。');
        }

        // LIFF初期化前にURLパラメータをsessionStorageに保存
        // OAuthリダイレクト後にenv/tokenが失われるのを防止
        const urlParams = new URLSearchParams(window.location.search);
        let hasDirectToken = false;
        try {
          const envParam = urlParams.get('env');
          if (envParam && isValidEnvironment(envParam)) {
            persistEnvironment(envParam);
          }
          const tokenParam = urlParams.get('token');
          if (tokenParam && tokenParam.length <= 256) {
            sessionStorage.setItem('flaro_liff_token', tokenParam);
            hasDirectToken = true;
          }
        } catch (e) {
          console.warn('[LIFF] URLパラメータの保存に失敗しました', e);
        }

        // liff.state からもパラメータを抽出（OAuthリダイレクト後）
        // 直接URLパラメータが存在する場合はliff.stateのトークンで上書きしない
        try {
          const liffState = urlParams.get('liff.state');
          if (liffState) {
            const stateParams = parseLiffStateParams(liffState);
            if (stateParams) {
              const envFromState = stateParams.get('env');
              if (envFromState && isValidEnvironment(envFromState)) {
                persistEnvironment(envFromState);
              }
              if (!hasDirectToken) {
                const tokenFromState = stateParams.get('token');
                if (tokenFromState && tokenFromState.length <= 256) {
                  sessionStorage.setItem('flaro_liff_token', tokenFromState);
                }
              }
            }
          }
        } catch (e) {
          console.warn('[LIFF] liff.stateパラメータの解析に失敗しました', e);
        }

        // PC外部ブラウザでのOAuthリダイレクトによるパスロスを防止
        // ルート(/)以外のパスの場合のみ保存（LinkPageフローに干渉しないため）
        if (window.location.pathname !== '/') {
          try {
            sessionStorage.setItem(
              'flaro_liff_return_path',
              JSON.stringify({
                path: window.location.pathname + window.location.search,
                timestamp: Date.now(),
              })
            );
          } catch (e) {
            console.warn(
              '[LIFF] sessionStorageへのパス保存に失敗しました。OAuthリダイレクト後にパスが復元されない可能性があります。',
              e
            );
          }
        }

        addLog('useLiff', 'liff_init_start', {
          liffId: liffId.substring(0, 8) + '...',
          url: window.location.href,
        });

        // 外部ブラウザ（Safari等）でも自動的にLINEログインを実行する
        await liff.init({
          liffId,
          withLoginOnExternalBrowser: true,
        });

        const isLoggedIn = liff.isLoggedIn();
        const isInClient = liff.isInClient();

        addLog('useLiff', 'liff_init_success', {
          isLoggedIn,
          isInClient,
        });

        // SDK情報を取得してログ
        try {
          addLog('useLiff', 'liff_sdk_info', {
            os: liff.getOS?.() ?? 'unknown',
            sdkVersion: liff.getVersion?.() ?? 'unknown',
            lineVersion: liff.getLineVersion?.() ?? 'unknown',
          });
        } catch {
          // SDK info retrieval is best-effort
        }

        // LINEにログインしていない場合、ログインを促す
        if (!isLoggedIn) {
          addLog('useLiff', 'liff_not_logged_in', { isInClient });
          setState({
            ...initialState,
            isInitialized: true,
            isInClient,
            isLoading: false,
          });
          return;
        }

        // ログイン成功時にsessionStorageのリターンパスをクリーンアップ
        try {
          sessionStorage.removeItem('flaro_liff_return_path');
        } catch (e) {
          console.warn('[LIFF] sessionStorageクリーンアップ失敗', e);
        }

        // プロフィール情報を取得
        const profile = await liff.getProfile();
        const decodedIdToken = liff.getDecodedIDToken();
        const email = decodedIdToken?.email || null;
        const accessToken = liff.getAccessToken();
        const idToken = liff.getIDToken();

        setState({
          isInitialized: true,
          isLoggedIn: true,
          isInClient,
          profile: {
            userId: profile.userId,
            displayName: profile.displayName,
            pictureUrl: profile.pictureUrl,
            statusMessage: profile.statusMessage,
          },
          email,
          accessToken,
          idToken,
          error: null,
          isLoading: false,
        });

        addLog('useLiff', 'liff_logged_in', {
          userId: profile.userId,
          hasAccessToken: !!accessToken,
          hasIdToken: !!idToken,
        });
      } catch (error) {
        console.error('LIFF initialization error:', error);

        const errorString = error instanceof Error ? error.message : String(error);

        addLog('useLiff', 'liff_init_error', {
          errorMessage: errorString,
          errorType: error instanceof Error ? error.constructor.name : typeof error,
        });

        // エラーメッセージを解析してユーザーフレンドリーなメッセージに変換
        let errorMessage = 'LIFF初期化に失敗しました';

        if (errorString.includes('Unable to load client features')) {
          // 外部ブラウザでのクライアント機能読み込みエラー
          errorMessage = 'このアプリはLINEアプリ内で開く必要があります。LINEアプリからアクセスしてください。';
        } else if (errorString.includes('INIT_FAILED')) {
          errorMessage = 'LIFFの初期化に失敗しました。ページを再読み込みしてください。';
        } else if (errorString.includes('INVALID_CONFIG')) {
          errorMessage = 'LIFF設定に問題があります。管理者にお問い合わせください。';
        }

        setState({
          ...initialState,
          isInitialized: true,
          isLoading: false,
          error: new Error(errorMessage),
        });
      }
    };

    initLiff();
  }, []);

  const login = useCallback(() => {
    if (liff.isLoggedIn()) {
      return;
    }
    const redirectUri = window.location.href;
    addLog('useLiff', 'liff_login_call', {
      redirectUri,
      currentUrl: window.location.href,
    });
    liff.login({ redirectUri });
  }, []);

  const logout = useCallback(() => {
    try {
      if (liff.isLoggedIn()) {
        liff.logout();
      }
    } catch {
      // LIFF未初期化時など liff.isLoggedIn() / liff.logout() がスローする場合がある
    }
    window.location.reload();
  }, []);

  const closeLiff = useCallback(() => {
    if (liff.isInClient()) {
      liff.closeWindow();
    } else {
      // ブラウザの場合はFLAROアプリにリダイレクト
      const flaroAppUrl = import.meta.env.VITE_FLARO_APP_URL;
      if (flaroAppUrl) {
        window.location.href = flaroAppUrl;
      }
    }
  }, []);

  const openWindow = useCallback((url: string, external = false) => {
    liff.openWindow({
      url,
      external,
    });
  }, []);

  return {
    ...state,
    login,
    logout,
    closeLiff,
    openWindow,
  };
};

export default useLiff;
